import { useEffect, useState } from 'react';

import { get } from '../requests/api';
import { getAgencyId } from '../utils';

export interface IAgencyFlags {
  isAccountsFeatureFlagEnabled: boolean;
  isPublicMapEnabled: boolean;
  isSubmissionTransferredMessageEnabled: boolean;
  isMobileAppEnabled: boolean;
  appleAuth0ConnectionName: string | null;
}

const DEFAULT_FLAGS: IAgencyFlags = {
  isAccountsFeatureFlagEnabled: false,
  isPublicMapEnabled: false,
  isSubmissionTransferredMessageEnabled: true,
  isMobileAppEnabled: false,
  appleAuth0ConnectionName: null,
};

export function useAgencyFlags(): { flags: IAgencyFlags; isLoading: boolean } {
  const [flags, setFlags] = useState<IAgencyFlags>(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const agencyId = getAgencyId();

    if (!agencyId) {
      setFlags(DEFAULT_FLAGS);
      setIsLoading(false);
      return () => { cancelled = true; };
    }

    get<{
      isAccountsFeatureFlagEnabled?: boolean;
      isPublicMapEnabled?: boolean;
      isSubmissionTransferredMessageEnabled?: boolean;
      isMobileAppEnabled?: boolean;
      appleAuth0ConnectionName?: string | null;
    }>({
      url: `agency/${agencyId}/flags`,
      headers: { 'x-agency-id': String(agencyId) },
    })
      .then((resp) => {
        if (!cancelled) {
          setFlags({
            isAccountsFeatureFlagEnabled: !!resp?.isAccountsFeatureFlagEnabled,
            isPublicMapEnabled: resp?.isPublicMapEnabled ?? false,
            isSubmissionTransferredMessageEnabled: resp?.isSubmissionTransferredMessageEnabled ?? true,
            isMobileAppEnabled: !!resp?.isMobileAppEnabled,
            appleAuth0ConnectionName: resp?.appleAuth0ConnectionName || null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setFlags(DEFAULT_FLAGS);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { flags, isLoading };
}
